'use client';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import uniqueId from 'lodash/uniqueId';
import moment from 'moment';
import React, { FC, useCallback, useState } from 'react';

import type { DownloadReport, SetShowSnackbar } from '../../types';

import { dayjs } from '../../utils/dayjs';
import { RedshiftFacilitySearch } from './redshiftFacilitySearch';
import { ReportActionButton } from './reportActionButton';

interface OutstandingBalancesReportProps {
	setShowSnackbar: SetShowSnackbar;
	downloadReport: DownloadReport;
	forTenant?: boolean;
	prefillAccountId?: string;
	prefillSubName?: string;
}

export const TransactionReportRedshiftForm: FC<OutstandingBalancesReportProps> = ({
	setShowSnackbar,
	downloadReport,
	prefillAccountId,
	prefillSubName,
	forTenant = false,
}) => {
	const [filterType, setFilterType] = useState<string>('Account ID');
	const [accountId, setAccountId] = useState<number | null>(null);
	const [subscriptionName, setSubscriptionName] = useState<string>();
	const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
	const [isDownloadingCsv, setIsDownloadingCsv] = useState<boolean>(false);
	const [selectedFacilities, setSelectedFacilities] = useState<any[]>([]);
	const [selectedAreas, setSelectedAreas] = useState<any[]>([]);
	const [selectedDistricts, setSelectedDistricts] = useState<any[]>([]);
	const [selectedBranches, setSelectedBranches] = useState<any[]>([]);
	const [idType, setIdType] = useState<string>('Invoice');
	const [idToSearch, setIdToSearch] = useState<string>('');
	const [timezone, setTimezone] = useState<string>(dayjs.tz.guess());
	const [startDate, setStartDate] = useState<string | null>();
	const [endDate, setEndDate] = useState<string | null>();

	const getDownloadParams = useCallback(() => {
		const baseParams = {
			accountId,
			subscriptionName,
			...(prefillAccountId && { accountId: prefillAccountId }),
			...(prefillSubName && { subscriptionName: prefillSubName }),
			...(selectedFacilities.length !== 0 && {
				facilities: selectedFacilities.map((facility) => ({
					id: facility.id,
					facilityName: facility.facilityName,
				})),
			}),
			...(selectedAreas.length !== 0 && {
				facilityAreas: selectedAreas.map((area) => area.id),
			}),
			...(selectedBranches.length !== 0 && {
				facilityBranches: selectedBranches.map((branch) => branch.id),
			}),
			...(selectedDistricts.length !== 0 && {
				facilityDistricts: selectedDistricts.map((district) => district.id),
			}),
			timezone,
			startDate,
			endDate,
			idType,
			idToSearch,
		};

		return {
			...baseParams,
		};
	}, [
		accountId,
		subscriptionName,
		prefillSubName,
		prefillAccountId,
		selectedFacilities,
		timezone,
		startDate,
		endDate,
		idType,
		idToSearch,
		selectedAreas,
		selectedBranches,
		selectedDistricts,
	]);

	const handleMultipleFacilityChange = (selections) => {
		setSelectedFacilities(selections);
	};

	const params = getDownloadParams();
	const handleValidation = useCallback(() => {
		const validations = [
			{
				message: `Please select a Start Date.`,
				failingCondition: !startDate,
			},
		];
		const failingValidations = validations.filter(({ failingCondition }) => failingCondition);
		if (failingValidations.length) {
			setShowSnackbar({
				message: failingValidations[0].message,
				severity: 'error',
				autoHideDuration: 3000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			return false;
		}
		return true;
	}, [setShowSnackbar, startDate]);

	const handleReset = useCallback(() => {
		setFilterType('');
		setAccountId(null);
		setStartDate(null);
		setEndDate(null);
		setSubscriptionName('');
		setSelectedAreas([]);
		setSelectedBranches([]);
		setSelectedDistricts([]);
		setSelectedFacilities([]);
	}, []);

	return (
		<Box sx={{ padding: 3 }}>
			<Grid container spacing={2}>
				<Grid item xs={3}>
					<FormControl fullWidth variant='outlined'>
						<InputLabel>Filter Options</InputLabel>
						<Select
							label='Filter Options'
							value={filterType || ''}
							onChange={(e) => setFilterType(e.target.value)}
							data-cy='report-type-select-menu'
						>
							{['Account ID', 'Subscription Name']?.map((filter) => (
								<MenuItem key={filter} value={filter}>
									{filter}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				{filterType === 'Account ID' && (
					<Grid item xs={3}>
						<TextField
							fullWidth
							variant='outlined'
							label='Account ID'
							disabled={forTenant}
							value={accountId || prefillAccountId || ''}
							onChange={(e) => setAccountId(Number(e.target.value))}
							inputProps={{ 'data-cy': 'account-id-input' }}
						/>
					</Grid>
				)}
				{filterType === 'Subscription Name' && (
					<Grid item xs={3}>
						<TextField
							fullWidth
							variant='outlined'
							label='Sub Name'
							disabled={forTenant}
							value={subscriptionName || prefillSubName || ''}
							onChange={(e) => setSubscriptionName(e.target.value)}
							inputProps={{ 'data-cy': 'sub-name-input' }}
						/>
					</Grid>
				)}
				<Grid item xs={3}>
					<FormControl fullWidth variant='outlined'>
						<InputLabel>ID Type</InputLabel>
						<Select
							label='ID Type'
							value={idType || ''}
							onChange={(e) => setIdType(e.target.value)}
							data-cy='report-type-select-menu'
						>
							{['Credit', 'Invoice']?.map((filter) => (
								<MenuItem key={filter} value={filter}>
									{filter}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={3}>
					<TextField
						fullWidth
						variant='outlined'
						label={idType === 'Invoice' ? 'Invoice ID' : 'Credit ID'}
						value={idToSearch}
						onChange={(e) => setIdToSearch(e.target.value)}
						inputProps={{ 'data-cy': 'sub-name-input' }}
					/>
				</Grid>
				<Grid item xs={12}>
					<RedshiftFacilitySearch
						selectedAreas={selectedAreas}
						setSelectedAreas={setSelectedAreas}
						selectedDistricts={selectedDistricts}
						setSelectedDistricts={setSelectedDistricts}
						selectedBranches={selectedBranches}
						setSelectedBranches={setSelectedBranches}
						selectedFacilities={selectedFacilities}
						setSelectedFacilities={setSelectedFacilities}
						handleMultipleFacilityChange={handleMultipleFacilityChange}
					/>
				</Grid>
				<Grid item xs={3}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='Start Date*'
							value={startDate}
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'as-of-date-picker' },
								},
							}}
							onChange={setStartDate}
						/>
					</LocalizationProvider>
				</Grid>
				<Grid item xs={3}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='End Date'
							value={endDate}
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'as-of-date-picker' },
								},
							}}
							onChange={setEndDate}
						/>
					</LocalizationProvider>
				</Grid>
				<Grid item xs={3}>
					<FormControl variant='filled' size='small'>
						<Select
							size='small'
							variant='outlined'
							id='timezone'
							value={timezone}
							onChange={(event) => setTimezone(event.target.value)}
							sx={{ height: '36px' }}
						>
							{moment.tz.zonesForCountry('US').map((tz) => (
								<MenuItem key={`${tz}-${uniqueId()}`} value={tz}>
									{tz}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			</Grid>
			<ReportActionButton
				downloadReport={(format: string) =>
					downloadReport(format, {
						reportType: 'transactionReport' as string,
						params,
						handleValidation,
						setIsDownloadingCsv,
						setIsDownloadingPdf,
					})
				}
				isDownloadingPdf={isDownloadingPdf}
				isDownloadingCsv={isDownloadingCsv}
				onReset={handleReset}
				showPdfButton={false}
			/>
		</Box>
	);
};
